import { JetView } from 'webix-jet'; // Importing JetView from webix-jet for creating a view component
import {
    devConfig, // Development configuration settings
    countries, // List of countries for filtering
    profile, // User profile information
    cashDesks, // Cash desk information
    agentList, // List of agents
} from 'config/config'; // Importing configuration settings from the config module

/**
 * Class representing a filter view for betting shops.
 * @extends JetView
 */
export default class FilterBetShopView extends JetView {
    /**
     * Defines the configuration for the filter view.
     * @returns {Object} The configuration object for the view.
     */
    config() {
        // Retrieve the localization service from the app
        const _ = this.app.getService('locale')._;
        // Define the layout for the left section of the UI
        const left = {
            margin: 5, // Set margin for the left section
            id: 'a1', // Unique identifier for the left section
            rows: [
                // Define rows within the left section
                {
                    margin: 15, // Set margin for the row
                    css: 'on-row', // CSS class for styling
                    responsive: 'a1', // Responsive design identifier
                    cols: [
                        // Define columns within the row
                        {
                            // Column for filtering by user, visibility based on permissions
                            hidden: devConfig.getPermAction('filterByUser', 'Add'),
                            css: 'prm-filterByUser', // CSS class for styling
                            height: 1, // Set height for the column
                        },
                        {
                            // Text input for User ID
                            view: 'text', // UI component type
                            name: 'UserId', // Name of the input field
                            type: 'number', // Input type
                            minWidth: 200, // Minimum width of the input
                            // Label with icon and localized text
                            label: "<span class='icon-drivers-license-o'></span>" + _('ID Usuario'),
                            hidden: !devConfig.getPermAction('filterByUser', 'Add'), // Visibility based on permissions
                            css: 'prm-filterByUser', // CSS class for styling
                            labelPosition: 'top', // Position of the label
                            placeholder: _('ID Usuario'), // Placeholder text
                        },
                        {
                            // Text input for Name
                            view: 'text', // UI component type
                            name: 'Name', // Name of the input field
                            minWidth: 200, // Minimum width of the input
                            // Label with icon and localized text
                            label: "<span class='icon-user'></span>" + _('Nombre'),
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Nombre'), // Placeholder text
                            hidden: !devConfig.getPermAction('filterByUser', 'Add'), // Visibility based on permissions
                            css: 'prm-filterByUser', // CSS class for styling
                        },
                        {
                            // Text input for Document number
                            view: 'text', // UI component type
                            name: 'Document', // Name of the input field
                            minWidth: 200, // Minimum width of the input
                            label: _('Número cédula'), // Label text
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Numero cédula'), // Placeholder text
                        },
                        {
                            // Text input for Login
                            view: 'text', // UI component type
                            name: 'Login', // Name of the input field
                            minWidth: 200, // Minimum width of the input
                            label: _('Login'), // Label text
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Login'), // Placeholder text
                            hidden: !devConfig.getPermAction('filterByUser', 'Add'), // Visibility based on permissions
                            css: 'prm-filterByUser', // CSS class for styling
                        },
                    ],
                },
                {
                    margin: 15, // Set margin for the row
                    css: 'on-row', // CSS class for styling
                    responsive: 'a1', // Responsive design identifier
                    cols: [
                        // Define columns within the row
                        {
                            // Rich select for country selection
                            view: 'richselect', // UI component type
                            name: 'CountrySelect', // Name of the input field
                            id: 'CountrySelect', // Unique identifier for the component
                            localId: 'regions:combo', // Local identifier for the component
                            minWidth: 200, // Minimum width of the input
                            // Label with icon and localized text
                            label: "<span class='icon-language'></span>" + _('País'),
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: {
                                // Options for the rich select
                                body: {
                                    // Template for displaying options
                                    template: function (obj) {
                                        return "<i class='flags " + obj.icon + "'></i>" + obj.value;
                                    },
                                    data: countries, // Data source for options
                                },
                            },
                            // Visibility based on country report and permissions
                            hidden: devConfig.getCountryReport() || !devConfig.getPermAction('filterByCountry', 'Add'),
                            css: 'prm-filterByCountry', // CSS class for styling
                        },
                        {
                            // Rich select for country selection (readonly)
                            view: 'richselect', // UI component type
                            name: 'CountrySelect2', // Name of the input field
                            localId: 'regions:combo', // Local identifier for the component
                            minWidth: 200, // Minimum width of the input
                            // Label with icon and localized text
                            label: "<span class='icon-language'></span>" + _('País'),
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            value: profile.Settings.ReportCountry, // Default value from profile settings
                            readonly: true, // Make the field readonly
                            options: {
                                // Options for the rich select
                                body: {
                                    // Template for displaying options
                                    template: function (obj) {
                                        return "<i class='flags " + obj.icon + "'></i>" + obj.value;
                                    },
                                    data: countries, // Data source for options
                                },
                            },
                            // Visibility based on country report and permissions
                            hidden: !devConfig.getCountryReport() || !devConfig.getPermAction('filterByCountry', 'Add'),
                            css: 'prm-filterByCountry', // CSS class for styling
                        },
                        {
                            // Rich select for state selection
                            view: 'richselect', // UI component type
                            name: 'State', // Name of the input field
                            value: 'A', // Default value
                            localId: 'bonusStates:combo', // Local identifier for the component
                            // Label with icon and localized text
                            label: "<span class='icon-state'></span>" + _('Estado'),
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            options: [
                                // Options for the rich select
                                {
                                    id: 'A', // Option ID
                                    value: _('Activo'), // Option label
                                },
                                {
                                    id: 'I', // Option ID
                                    value: _('Inactivo'), // Option label
                                },
                            ],
                        },
                        {
                            // Text input for datepicker
                            view: 'text', // UI component type
                            name: 'Datepicker', // Name of the input field
                            id: 'Datepicker', // Unique identifier for the component
                            css: 'reportrange2', // CSS class for styling
                            // Label with icon and localized text
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha de Creación'),
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            on: {
                                // Event handlers
                                onFocus: function (o, e, v) {
                                    devConfig.setDateModal(lang); // Set date modal on focus
                                },
                            },
                        },
                        {
                            // Datepicker for selecting a date
                            view: 'datepicker', // UI component type
                            name: 'Date', // Name of the input field
                            id: 'Date', // Unique identifier for the component
                            stringResult: true, // Return date as a string
                            // Label with icon and localized text
                            label: "<span class='webix_icon icon-calendar'></span>" + _('Fecha de Creación'),
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            value: moment().format('YYYY-MM-DD'), // Default value as today's date
                            format: '%d %M %Y', // Date format
                            suggest: {
                                // Suggest options for the datepicker
                                type: 'calendar', // Suggest type
                                body: {
                                    maxDate: new Date(), // Maximum selectable date
                                },
                            },
                        },
                        {
                            // Combo for selecting region name
                            view: 'combo', // UI component type
                            name: 'RegionName', // Name of the input field
                            minWidth: 200, // Minimum width of the input
                            label: _('Región Perfil'), // Label text
                            labelPosition: 'top', // Position of the label
                            placeholder: _('Click para seleccionar '), // Placeholder text
                            hidden: profile.PartnerSelected != 8, // Visibility based on profile condition
                            options: [
                                // Options for the combo
                                { id: 'CENTRO', value: _('CENTRO') }, // Option for CENTRO
                                { id: 'COSTA', value: _('COSTA') }, // Option for COSTA
                                { id: 'AUSTRO', value: _('AUSTRO') }, // Option for AUSTRO
                            ],
                        },
                    ],
                },
            ],
        };
        const left2 = {
            /**
             * Configuration object for the left section of the UI.
             * Contains layout properties and form fields for user input.
             */
            margin: 5,
            id: 'a2',
            rows: [
                {
                    /**
                     * Row configuration with margin and CSS class.
                     * Contains columns for contact information.
                     */
                    margin: 15,
                    css: 'on-row',
                    responsive: 'a2',
                    cols: [
                        {
                            /**
                             * Text input for the contact name.
                             * Includes label, placeholder, and minimum width.
                             */
                            view: 'text',
                            name: 'ContactName',
                            minWidth: 200,
                            label: "<span class='icon-user'></span>" + _('Nombre del contacto'),
                            labelPosition: 'top',
                            placeholder: _('Nombre del contacto'),
                        },
                        {
                            /**
                             * Text input for the email address.
                             */
                            view: 'text',
                            name: 'Email',
                            minWidth: 200,
                            label: _('Correo electrónico'),
                            labelPosition: 'top',
                            placeholder: _('Correo electrónico'),
                        },
                        {
                            /**
                             * Text input for the manager's phone number.
                             */
                            view: 'text',
                            name: 'ManagerPhone',
                            minWidth: 200,
                            label: _('Teléfono'),
                            labelPosition: 'top',
                            placeholder: _('Teléfono'),
                        },
                        {
                            /**
                             * Text input for the IP address.
                             */
                            view: 'text',
                            name: 'Ip',
                            minWidth: 200,
                            label: "<span class='icon-ip'></span>" + _('Dirección IP'),
                            labelPosition: 'top',
                            placeholder: _('Direccion IP '),
                        },
                    ],
                },
                {
                    /**
                     * Row configuration for geographical information.
                     */
                    margin: 15,
                    css: 'on-row',
                    responsive: 'a2',
                    cols: [
                        {
                            /**
                             * Text input for the region or province.
                             */
                            view: 'text',
                            name: 'Region',
                            minWidth: 200,
                            label: "<span class='icon-language'></span>" + _('Provincia/Región'),
                            labelPosition: 'top',
                            placeholder: _('Provincia/Región'),
                        },
                        {
                            /**
                             * Text input for the city name.
                             */
                            view: 'text',
                            name: 'CityName',
                            minWidth: 200,
                            label: "<span class='icon-language'></span>" + _('Ciudad'),
                            labelPosition: 'top',
                            placeholder: _('Ciudad'),
                        },
                        {
                            /**
                             * Text input for the district name.
                             */
                            view: 'text',
                            name: 'District',
                            minWidth: 200,
                            label: "<span class='icon-language'></span>" + _('Barrio'),
                            labelPosition: 'top',
                            placeholder: _('Barrio'),
                        },
                        {
                            /**
                             * Combo box for selecting a zone.
                             */
                            view: 'combo',
                            name: 'Zone',
                            minWidth: 200,
                            id: 'Zone',
                            label: _('Zona'),
                            labelPosition: 'top',
                            options: [],
                            placeholder: _('Click para seleccionar '),
                        },
                    ],
                },
                {
                    /**
                     * Row configuration for address and sorting options.
                     */
                    margin: 15,
                    css: 'on-row',
                    responsive: 'a2',
                    cols: [
                        {
                            /**
                             * Text input for the address.
                             */
                            view: 'text',
                            name: 'Address',
                            minWidth: 200,
                            label: "<span class='icon-ip'></span>" + _('Dirección'),
                            labelPosition: 'top',
                            placeholder: _('Dirección'),
                        },
                        {
                            /**
                             * Rich select for IP identification program status.
                             */
                            view: 'richselect',
                            name: 'IPIdentification',
                            value: '',
                            localId: 'IPIdentification:combo',
                            label: "<span class='icon-state'></span>" + _('Programa de identificación de IP Activo'),
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '),
                            options: [
                                {
                                    id: '',
                                    value: _('Todos'),
                                },
                                {
                                    id: '1',
                                    value: _('Si'),
                                },
                                {
                                    id: '0',
                                    value: _('No'),
                                },
                            ],
                        },
                        {
                            /**
                             * Combo box for sorting options.
                             */
                            view: 'combo',
                            name: 'Sort',
                            minWidth: 200,
                            label: _('Ordenar por'),
                            labelPosition: 'top',
                            placeholder: _('Click para seleccionar '),
                            options: [
                                { id: '1', value: _('Fecha de creación') },
                                { id: '2', value: _('ID') },
                                { id: '3', value: _('Login') },
                                { id: '4', value: _('Nombre') },
                            ],
                            value: '1',
                        },
                        {
                            /**
                             * Combo box for sorting order (ascending/descending).
                             */
                            view: 'combo',
                            name: 'Dir',
                            minWidth: 200,
                            label: _('Orden'),
                            labelPosition: 'top',
                            options: [
                                { id: '0', value: _('Ascendente') },
                                { id: '1', value: _('Descendente') },
                            ],
                            value: '0',
                        },
                    ],
                },
                {
                    /**
                     * Row configuration for agent-related fields.
                     */
                    margin: 15,
                    css: 'on-row',
                    responsive: 'a2',
                    cols: [
                        {
                            /**
                             * Text input for the dealer ID.
                             * Visibility is controlled by permission settings.
                             */
                            view: 'text',
                            name: 'UserIdAgent',
                            type: 'number',
                            minWidth: 200,
                            label: "<span class='icon-drivers-license-o'></span>" + _('ID Concesionario'),
                            labelPosition: 'top',
                            placeholder: _('ID Concesionario'),
                            hidden: !devConfig.getPermAction('filterByAgent', 'Add'),
                            css: 'prm-filterByAgent',
                        },
                        {
                            /**
                             * Text input for the sub-dealer ID.
                             * Visibility is controlled by permission settings.
                             */
                            view: 'text',
                            name: 'UserIdAgent2',
                            type: 'number',
                            minWidth: 200,
                            label: "<span class='icon-drivers-license-o'></span>" + _('Subconcesionario'),
                            labelPosition: 'top',
                            placeholder: _('Subconcesionario'),
                            hidden: !devConfig.getPermAction('filterByAgent2', 'Add'),
                            css: 'prm-filterByAgent2',
                        },
                        {
                            /**
                             * Gravity setting for layout adjustment.
                             */
                            id: 'grav2',
                            gravity: 2,
                            minWidth: 200,
                        },
                    ],
                },
            ],
        };
        /**
         * Configuration object for the upper section layout.
         * @type {Object}
         * @property {string} css - CSS class for styling the container.
         * @property {Array} cols - Array containing column definitions, including 'left'.
         */
        const upper_section = {
            css: 'father-container',
            cols: [left],
        };

        /**
         * Configuration object for the second upper section layout.
         * @type {Object}
         * @property {string} css - CSS class for styling the container.
         * @property {Array} rows - Array containing row definitions, including a margin and columns.
         */
        const upper_section2 = {
            css: 'father-container',
            rows: [
                {
                    margin: 10, // Margin around the row
                    cols: [left2], // Array containing column definitions, including 'left2'
                },
            ],
        };

        /**
         * Configuration object for the accordion section layout.
         * @type {Object}
         * @property {Array} rows - Array containing row definitions for the accordion.
         */
        const accordion_section2 = {
            rows: [
                {
                    multi: true, // Allows multiple items to be expanded
                    view: 'accordion', // Specifies the view type as accordion
                    css: 'betShop', // CSS class for styling the accordion
                    collapsed: true, // Initial state of the accordion (collapsed)
                    paddingY: 10, // Vertical padding for the accordion
                    type: 'clean', // Type of accordion styling
                    rows: [
                        {
                            header: _('Detalles'), // Header text for the accordion section
                            body: {
                                padding: 10, // Padding for the body of the accordion
                                rows: [upper_section2], // Rows to be included in the body, referencing upper_section2
                            },
                        },
                    ],
                },
            ],
        };
        /**
         * Converts a file to a Base64 string using FileReader.
         * @param {File} file - The file to be converted to Base64.
         * @returns {Promise<string>} A promise that resolves with the Base64 string of the file.
         */
        function getBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader(); // Create a new FileReader instance.
                reader.readAsDataURL(file); // Read the file as a data URL.
                reader.onload = () => resolve(reader.result); // Resolve the promise with the result on load.
                reader.onerror = (error) => reject(error); // Reject the promise on error.
            });
        }
        let base64CSV = ''; // Variable to store the Base64 representation of the CSV file.
        let base64CSV33 = ''; // Unused variable for potential future use.
        let popupAdd = webix.ui({
            // Create a Webix UI window for adding contingencies.
            view: 'window',
            id: 'MassiveContingency',
            fullscreen: screen.width < 500 ? true : false, // Fullscreen if screen width is less than 500px.
            head: {
                view: 'toolbar',
                cols: [
                    { view: 'label', label: _('Activar Contingencia Masiva') }, // Label for the window title.
                    {
                        view: 'icon',
                        icon: 'wxi-close', // Close icon for the window.
                        click: function () {
                            this.getTopParentView().hide(); // Hide the window on click.
                        },
                    },
                ],
            },
            position: 'center', // Center the window on the screen.
            modal: true, // Make the window modal.
            body: {
                view: 'form',
                id: 'addContingencyForm', // ID for the form.
                elements: [
                    {
                        margin: 10,
                        rows: [
                            {
                                view: 'uploader', // File uploader component.
                                id: 'uploader', // ID for the uploader.
                                value: _('Cargar archivo CSV'), // Initial label for the uploader.
                                on: {
                                    onBeforeFileAdd: function (upload) {
                                        getBase64(upload.file).then(
                                            // Convert the uploaded file to Base64.
                                            (data) => (base64CSV = data), // Store the Base64 data.
                                        );
                                        $$('uploader').define(
                                            // Update the uploader label with the file name.
                                            'label',
                                            upload.name,
                                        );
                                        $$('uploader').refresh(); // Refresh the uploader to reflect changes.
                                    },
                                },
                            },
                            {
                                view: 'multiselect', // Multi-select dropdown for selecting contingency types.
                                label: _('Tipo'), // Label for the multi-select.
                                labelPosition: 'top', // Position the label at the top.
                                required: true, // Mark the field as required.
                                name: 'Contingencies', // Name for the multi-select.
                                options: [
                                    // Options for the multi-select.
                                    { id: '0', value: _('Contingencia Total') },
                                    {
                                        id: '1',
                                        value: _('Contingencia Deportivas'),
                                    },
                                    {
                                        id: '2',
                                        value: _('Contingencia Casino'),
                                    },
                                    {
                                        id: '3',
                                        value: _('Contingencia Casino en Vivo'),
                                    },
                                    {
                                        id: '4',
                                        value: _('Contingencia Virtuales'),
                                    },
                                    { id: '5', value: _('Contingencia Poker') },
                                    {
                                        id: '6',
                                        value: _('Contingencia Retiro'),
                                    },
                                    {
                                        id: '7',
                                        value: _('Contingencia Depósito'),
                                    },
                                    { id: '8', value: _('Abusador de bonos') },
                                ],
                            },
                            {
                                view: 'textarea', // Text area for entering a description.
                                labelPosition: 'top', // Position the label at the top.
                                label: _('Descripción:'), // Label for the text area.
                                name: 'Description', // Name for the text area.
                                labelWidth: 150, // Width of the label.
                                required: true, // Mark the field as required.
                                height: 150, // Height of the text area.
                            },
                        ],
                    },
                    {
                        cols: [
                            {
                                view: 'button', // Cancel button.
                                value: _('Cancelar'), // Label for the button.
                                click: function () {
                                    this.getTopParentView().hide(); // Hide the window on click.
                                    $$('addContingencyForm').setValues(
                                        // Reset the form values.
                                        $$('addContingencyForm').getCleanValues(),
                                    );
                                    $$('uploader').define(
                                        // Reset the uploader label.
                                        'label',
                                        _('Cargar archivo CSV'),
                                    );
                                    $$('uploader').refresh(); // Refresh the uploader to reflect changes.
                                    base64CSV = ''; // Clear the Base64 CSV variable.
                                },
                            },
                            {
                                view: 'button', // Save button.
                                type: 'form', // Button type indicating it submits the form.
                                value: _('Guardar'), // Label for the button.
                                click: function ($e) {
                                    var values = $$('addContingencyForm').getValues(); // Get form values.
                                    if (
                                        $$('addContingencyForm').validate() && // Validate the form.
                                        base64CSV != '' // Check if Base64 CSV is not empty.
                                    ) {
                                        webix // Perform an AJAX request to save the contingency.
                                            .ajax()
                                            .headers({
                                                'Content-type': 'application/json', // Set content type to JSON.
                                            })
                                            .post(
                                                devConfig.geturlApi() + // API endpoint for saving the contingency.
                                                    'Client/MassiveContingency',
                                                '{"CSV":"' +
                                                    base64CSV +
                                                    '","Type":"1","Description":"' +
                                                    values.Description +
                                                    '","Contingencies":"' +
                                                    values.Contingencies +
                                                    '"}', // JSON payload for the request.
                                                {
                                                    error: function (text, data, XmlHttpRequest) {
                                                        $$('addContingencyForm').setValues(
                                                            // Reset the form values on error.
                                                            $$('addContingencyForm').getCleanValues(),
                                                        );
                                                        webix.message(
                                                            // Show error message.
                                                            'Error! ',
                                                        );
                                                    },
                                                    success: function (text, data, XmlHttpRequest) {
                                                        text = JSON.parse(text); // Parse the response text.
                                                        if (text.HasError) {
                                                            // Check for errors in the response.
                                                            $$('addContingencyForm').setValues(
                                                                // Reset the form values on error.
                                                                $$('addContingencyForm').getCleanValues(),
                                                            );
                                                            webix.message(
                                                                // Show error message with alert.
                                                                'Error: ' + text.AlertMessage,
                                                            );
                                                        } else {
                                                            $$('addContingencyForm').setValues(
                                                                // Reset the form values on success.
                                                                $$('addContingencyForm').getCleanValues(),
                                                            );
                                                            webix.message(
                                                                // Show success message.
                                                                'Contingencia masiva creada',
                                                            );
                                                        }
                                                    },
                                                },
                                            );
                                        this.getTopParentView().hide(); // Hide the window after saving.
                                    } else {
                                        webix.message({
                                            // Show alert if validation fails.
                                            title: _('¡Mensaje!'),
                                            type: 'alert-error',
                                            text: _('Completa los campos...'),
                                        });
                                    }
                                },
                            },
                        ],
                    },
                ],
                elementsConfig: {
                    on: {
                        onChange: function () {
                            this.validate(); // Validate the form on change.
                        },
                    },
                },
            },
        });
        // Create a Webix UI window for deactivating massive contingencies
        let popupAdd2 = webix.ui({
            view: 'window', // Define the view type as a window
            id: 'DesactiveMassiveContingency', // Unique identifier for the window
            fullscreen: screen.width < 500 ? true : false, // Fullscreen mode for small screens
            head: {
                view: 'toolbar', // Toolbar at the top of the window
                cols: [
                    {
                        view: 'label', // Label for the window title
                        label: _('Desactivar Contingencia Masiva'), // Translated title
                    },
                    {
                        view: 'icon', // Close icon in the toolbar
                        icon: 'wxi-close', // Icon type
                        click: function () {
                            // Click event to close the window
                            this.getTopParentView().hide(); // Hide the window
                        },
                    },
                ],
            },
            position: 'center', // Center the window on the screen
            modal: true, // Make the window modal
            body: {
                view: 'form', // Form inside the window
                id: 'addDesactiveContingencyForm', // Unique identifier for the form
                elements: [
                    {
                        margin: 10, // Margin around the form elements
                        rows: [
                            {
                                view: 'uploader', // File uploader component
                                id: 'uploader2', // Unique identifier for the uploader
                                value: _('Cargar archivo CSV'), // Initial label for the uploader
                                on: {
                                    onBeforeFileAdd: function (upload) {
                                        // Event before a file is added
                                        getBase64(upload.file).then(
                                            // Convert file to Base64
                                            (data) => (base64CSV33 = data), // Store Base64 data
                                        );
                                        $$('uploader2').define(
                                            // Update uploader label with file name
                                            'label',
                                            upload.name,
                                        );
                                        $$('uploader2').refresh(); // Refresh the uploader to show the new label
                                    },
                                },
                            },
                            {
                                view: 'combo', // Dropdown selection for contingency type
                                label: _('Tipo'), // Label for the combo
                                labelPosition: 'top', // Position of the label
                                required: true, // Mark as required
                                name: 'Contingency', // Name of the field
                                options: [
                                    // Options for the combo
                                    { id: '0', value: _('Contingencia Total') },
                                    {
                                        id: '1',
                                        value: _('Contingencia Deportivas'),
                                    },
                                    {
                                        id: '2',
                                        value: _('Contingencia Casino'),
                                    },
                                    {
                                        id: '3',
                                        value: _('Contingencia Casino en Vivo'),
                                    },
                                    {
                                        id: '4',
                                        value: _('Contingencia Virtuales'),
                                    },
                                    { id: '5', value: _('Contingencia Poker') },
                                    {
                                        id: '6',
                                        value: _('Contingencia Retiro'),
                                    },
                                    {
                                        id: '7',
                                        value: _('Contingencia Depósito'),
                                    },
                                    { id: '8', value: _('Abusador de bonos') },
                                ],
                            },
                            {
                                view: 'textarea', // Text area for description
                                labelPosition: 'top', // Position of the label
                                label: _('Descripción:'), // Label for the text area
                                name: 'Description', // Name of the field
                                labelWidth: 150, // Width of the label
                                required: true, // Mark as required
                                height: 150, // Height of the text area
                            },
                        ],
                    },
                    {
                        cols: [
                            // Buttons for form actions
                            {
                                view: 'button', // Cancel button
                                value: _('Cancelar'), // Button label
                                click: function () {
                                    // Click event for cancel button
                                    this.getTopParentView().hide(); // Hide the window
                                    $$('addDesactiveContingencyForm').setValues(
                                        // Reset form values
                                        $$('addDesactiveContingencyForm').getCleanValues(),
                                    );
                                    $$('uploader2').define(
                                        // Reset uploader label
                                        'label',
                                        _('Cargar archivo CSV'),
                                    );
                                    $$('uploader2').refresh(); // Refresh uploader
                                    base64CSV33 = ''; // Clear Base64 data
                                },
                            },
                            {
                                view: 'button', // Save button
                                type: 'form', // Button type for form submission
                                value: _('Guardar'), // Button label
                                click: function ($e) {
                                    // Click event for save button
                                    var values = $$('addDesactiveContingencyForm').getValues(); // Get form values
                                    if (
                                        // Validate form and check if Base64 data is present
                                        $$('addDesactiveContingencyForm').validate() &&
                                        base64CSV33 != ''
                                    ) {
                                        webix // Perform AJAX request to save data
                                            .ajax()
                                            .headers({
                                                'Content-type': 'application/json', // Set content type
                                            })
                                            .post(
                                                devConfig.geturlApi() + // API endpoint
                                                    'Client/DesactiveMassiveContingency',
                                                '{"CSV":"' + // JSON payload
                                                    base64CSV33 +
                                                    '","Type":"1","Description":"' +
                                                    values.Description +
                                                    '","Contingencies":"' +
                                                    values.Contingency +
                                                    '"}',
                                                {
                                                    error: function (
                                                        // Error callback
                                                        text,
                                                        data,
                                                        XmlHttpRequest,
                                                    ) {
                                                        $$('addDesactiveContingencyForm').setValues(
                                                            // Reset form values on error
                                                            $$('addDesactiveContingencyForm').getCleanValues(),
                                                        );
                                                        webix.message(
                                                            // Show error message
                                                            'Error! ',
                                                        );
                                                    },
                                                    success: function (
                                                        // Success callback
                                                        text,
                                                        data,
                                                        XmlHttpRequest,
                                                    ) {
                                                        text = JSON.parse(text); // Parse response text
                                                        if (text.HasError) {
                                                            // Check for errors in response
                                                            $$('addDesactiveContingencyForm').setValues(
                                                                // Reset form values on error
                                                                $$('addDesactiveContingencyForm').getCleanValues(),
                                                            );
                                                            webix.message(
                                                                // Show error message
                                                                'Error: ' + text.AlertMessage,
                                                            );
                                                        } else {
                                                            $$('addDesactiveContingencyForm').setValues(
                                                                // Reset form values on success
                                                                $$('addDesactiveContingencyForm').getCleanValues(),
                                                            );
                                                            webix.message(
                                                                // Show success message
                                                                'Contingencia masiva creada',
                                                            );
                                                        }
                                                    },
                                                },
                                            );
                                        this.getTopParentView().hide(); // Hide the window after submission
                                    } else {
                                        webix.message({
                                            // Show alert if validation fails
                                            title: _('¡Mensaje!'),
                                            type: 'alert-error',
                                            text: _('Completa los campos...'),
                                        });
                                    }
                                },
                            },
                        ],
                    },
                ],
                elementsConfig: {
                    // Configuration for form elements
                    on: {
                        onChange: function () {
                            // Validate form on change
                            this.validate();
                        },
                    },
                },
            },
        });
        const buttons = {
            /** Unique identifier for the button group */
            id: 'b2',
            /** Height of the button group */
            height: 70,
            /** Array of rows containing button configurations */
            rows: [
                {
                    /** Margin around the row */
                    margin: 10,
                    /** Responsive identifier for the row */
                    responsive: 'b2',
                    /** Array of columns containing button definitions */
                    cols: [
                        { id: 'grav', gravity: 4 }, // Column configuration with gravity

                        {
                            /** Minimum width for the button */
                            minWidth: 250,
                            /** Type of view for the button */
                            view: 'button',
                            /** Button label */
                            value: _('Desactivar Contingencia'),
                            /** Button type */
                            type: 'form',
                            /** Width of the button based on screen size */
                            width: screen == 'small' ? 0 : 200,
                            /** Visibility based on permission configuration */
                            hidden: !devConfig.getPermAction('DesactiveMassiveContingencyPv', 'Add'),
                            /** CSS class for styling */
                            css: 'prm-MassiveContingency',
                            /** Click event handler for the button */
                            click: () => {
                                popupAdd2.show(); // Show popup for deactivating contingency
                            },
                        },
                        {
                            /** Minimum width for the button */
                            minWidth: 250,
                            /** Type of view for the button */
                            view: 'button',
                            /** Button label */
                            value: _('Activar Contingencia'),
                            /** Button type */
                            type: 'form',
                            /** Width of the button based on screen size */
                            width: screen == 'small' ? 0 : 200,
                            /** Visibility based on permission configuration */
                            hidden: !devConfig.getPermAction('MassiveContingencyPv', 'Add'),
                            /** CSS class for styling */
                            css: 'prm-MassiveContingency',
                            /** Click event handler for the button */
                            click: () => {
                                popupAdd.show(); // Show popup for activating contingency
                            },
                        },
                        {
                            /** Type of view for the button */
                            view: 'button',
                            /** Button label */
                            value: _('Añadir Punto'),
                            /** Button type */
                            type: 'form',
                            /** Width of the button based on screen size */
                            width: screen == 'small' ? 0 : 130,
                            /** Minimum width for the button */
                            minWidth: 200,
                            /** Visibility based on permission configuration */
                            hidden: !devConfig.getPermAction('betShopAdd', 'Add'),
                            /** CSS class for styling */
                            css: 'prm-betShopAdd',
                            /** Click event handler for the button */
                            click: () => {
                                /** Check current page and show appropriate view */
                                if (this.getUrl()[0].page !== 'betShopManagement')
                                    this.show('addBetShopManagement/betShopManagement.addInformationBetShop');
                                else this.show('/betShopManagement.addinformationBetShop');
                            },
                        },
                        {
                            /** Type of view for the button */
                            view: 'button',
                            /** Button label */
                            value: _('Limpiar'),
                            /** Width of the button based on screen size */
                            width: screen == 'small' ? 0 : 100,
                            /** Minimum width for the button */
                            minWidth: 200,
                            /** Click event handler for the button */
                            click: () => {
                                this.getRoot().clear(); // Clear the root component
                            },
                        },
                        {
                            /** Type of view for the button */
                            view: 'button',
                            /** Button label */
                            value: _('Consultar'),
                            /** Hotkey for the button */
                            hotkey: 'enter',
                            /** Button type */
                            type: 'form',
                            /** Width of the button based on screen size */
                            width: screen == 'small' ? 0 : 100,
                            /** Minimum width for the button */
                            minWidth: 200,
                            /** Click event handler for the button */
                            click: () => {
                                /** Retrieve values from the root component */
                                const filterdata = this.getRoot().getValues();
                                /** Check if country report is enabled and set country filter */
                                if (devConfig.getCountryReport()) {
                                    filterdata.CountrySelect = profile.Settings.ReportCountry;
                                }
                                /** Remove unnecessary filter fields */
                                delete filterdata['CountrySelect2'];
                                if (profile.typeDate !== 2) {
                                    delete filterdata['Date'];
                                } else {
                                    if (true) {
                                        delete filterdata['Datepicker'];
                                    }
                                }
                                /** Trigger filter event with the collected filter data */
                                this.app.callEvent('betShop:filter', [filterdata]);
                                /** Collapse and scroll to specific elements */
                                $$('b5').collapse();
                                $$('a5').scrollTo(100, 0);
                            },
                        },
                    ],
                },
            ],
        };
        const buttons2 = {
            // Configuration for button properties
            padding: 10,
            cols: [
                {
                    // Button to add a new betting shop
                    view: 'button',
                    value: _('Añadir Punto'), // Button label
                    type: 'form', // Indicates that this button submits the form
                    hidden: !devConfig.getPermAction('betShopAdd', 'Add'), // Visibility based on permissions
                    css: 'prm-betShopAdd', // CSS class for styling
                    click: () => {
                        $$('filterPopup').hide(); // Hides the filter popup
                        // Navigates to the appropriate page based on the current URL
                        if (this.getUrl()[0].page !== 'betShopManagement')
                            this.show('addBetShopManagement/betShopManagement.addInformationBetShop');
                        else this.show('/betShopManagement.addinformationBetShop');
                    },
                },
                {
                    // Button to clear the form
                    view: 'button',
                    value: _('Limpiar'), // Button label
                    click: () => {
                        $$('myForm2').clear(); // Clears the form fields
                    },
                },
                {
                    // Button to consult/filter data
                    view: 'button',
                    value: _('Consultar'), // Button label
                    hotkey: 'enter', // Assigns the Enter key as a shortcut
                    type: 'form', // Indicates that this button submits the form
                    click: () => {
                        const filterdata = $$('myForm2').getValues(); // Retrieves form values
                        // Checks if country report is enabled and sets the country filter
                        if (devConfig.getCountryReport()) {
                            filterdata.CountrySelect = profile.Settings.ReportCountry;
                        }
                        delete filterdata['CountrySelect2']; // Removes unnecessary filter
                        // Conditional deletion of date filters based on profile type
                        if (profile.typeDate !== 2) {
                            delete filterdata['Date'];
                        } else {
                            if (true) {
                                delete filterdata['Datepicker'];
                            }
                        }
                        // Triggers the filter event with the collected filter data
                        this.app.callEvent('betShop:filter', [filterdata]);
                    },
                },
            ],
        };
        // Define the accordion_section object representing a UI component
        const accordion_section = {
            // Specifies the type of the section layout
            type: 'wide',
            // Contains an array of rows for the accordion
            rows: [
                {
                    // Allows multiple sections to be expanded at once
                    multi: true,
                    // Defines the view type as an accordion
                    view: 'accordion',
                    // CSS class for styling the accordion
                    css: 'betShop',
                    // Indicates whether the accordion is initially collapsed
                    collapsed: false,
                    // Specifies the type of the accordion's layout
                    type: 'clean',
                    // Contains an array of rows within the accordion
                    rows: [
                        {
                            // Header text for the accordion section
                            header: _('Filtros'),
                            // Unique identifier for the section
                            id: 'b5',
                            // Body of the accordion section containing padding and rows
                            body: {
                                // Padding around the body content
                                padding: 10,
                                // Rows contained within the body of the accordion section
                                rows: [
                                    upper_section, // Reference to an upper section component
                                    accordion_section2, // Reference to another accordion section
                                    buttons, // Reference to a buttons component
                                ],
                            },
                        },
                    ],
                },
            ],
        };
        /**
         * Configuration object for the accordion section in the UI.
         * Contains a scrollable view with padding and rows for upper section and accordion_section2.
         */
        const accordion_section3 = {
            view: 'scrollview', // Defines a scrollable view
            scroll: 'xy', // Enables scrolling in both x and y directions
            body: {
                padding: 10, // Sets padding for the body
                rows: [upper_section, accordion_section2], // Contains the upper section and another accordion section
            },
        };

        /**
         * Creates a filter popup window using Webix UI.
         * The window is modal and can be moved around.
         */
        const filterPopup = webix.ui({
            view: 'window', // Specifies the view type as a window
            move: true, // Allows the window to be moved
            id: 'filterPopup', // Unique identifier for the popup
            fullscreen: true, // Sets the window to fullscreen mode
            head: {
                view: 'toolbar', // Toolbar at the top of the window
                cols: [
                    { view: 'label', label: _('Filtros'), align: 'center' }, // Label for the filters section
                    {
                        view: 'icon', // Icon button to close the window
                        icon: 'wxi-close', // Icon type for closing
                        click: function () {
                            // Click event handler for the icon
                            this.getTopParentView().hide(); // Hides the popup window
                        },
                    },
                ],
            },
            modal: true, // Makes the window modal
            position: 'center', // Centers the window on the screen
            body: {
                view: 'form', // Specifies the body as a form
                id: 'myForm2', // Unique identifier for the form
                type: 'clean', // Sets the form type to clean
                rows: [accordion_section3, buttons2], // Contains the accordion section and buttons
                rules: {
                    fname: webix.rules.isNotEmpty, // Validation rule to ensure fname is not empty
                },
            },
        });
        /**
         * Returns a configuration object for a Webix form.
         * @returns {Object} The form configuration object.
         */
        return {
            /**
             * Unique identifier for the form.
             * @type {string}
             */
            id: 'filter',

            /**
             * Specifies the type of view.
             * @type {string}
             */
            view: 'form',

            /**
             * Defines the form type as clean.
             * @type {string}
             */
            type: 'clean',

            /**
             * Contains the layout structure of the form.
             * @type {Array}
             */
            rows: [
                /**
                 * Conditional rendering of the accordion section or a spacer based on screen dimensions.
                 * @type {Object}
                 */
                screen.width > 500 && screen.height > 500
                    ? accordion_section
                    : { id: 'right2', height: 1, width: screen.width - 54 },
            ],

            /**
             * Validation rules for the form fields.
             * @type {Object}
             */
            rules: {
                /**
                 * Rule to ensure the 'fname' field is not empty.
                 * @type {Function}
                 */
                fname: webix.rules.isNotEmpty,
            },
        }; // Placeholder for the configuration content
    }

    /**
     * Initializes the filter view.
     * @param {Object} grid - The grid component to be initialized.
     */
    init(grid) {
        webix.ready(function () {
            // Check if the screen width is less than 500 pixels
            if (screen.width < 500) {
                // No action defined for small screens
            }
            // Check if the profile typeDate is not equal to 2
            if (profile.typeDate !== 2) {
                // Hide the 'Date' element and show the 'Datepicker' element
                $$('Date').hide();
                $$('Datepicker').show();
            } else {
                // If profile typeDate is 2, show 'Date' and hide 'Datepicker'
                if (true) {
                    $$('Date').show();
                    $$('Datepicker').hide();
                }
            }
            // Attach an event listener for item clicks on the 'Zone' element
            $$('Zone').attachEvent('onItemClick', function (id, e) {
                // Make an AJAX POST request to get zones
                webix
                    .ajax()
                    .headers({
                        'Content-type': 'application/json', // Set content type to JSON
                    })
                    .post(devConfig.geturlApi() + 'BetShop/GetZones', '', {
                        // Define error handling for the AJAX request
                        error: function (text, data, XmlHttpRequest) {
                            webix.message(_('Error! ')); // Show error message
                        },
                        // Define success handling for the AJAX request
                        success: function (text, data, XmlHttpRequest) {
                            var data = JSON.parse(text); // Parse the JSON response
                            var optionsZones = $$('Zone').getList(); // Get the list of zones
                            optionsZones.clearAll(); // Clear existing options
                            optionsZones.parse(data.Data); // Parse and add new options
                        },
                    });
            });
            // Set a timeout to clear the value of the 'Datepicker' after 10 milliseconds
            setTimeout(() => {
                $$('Datepicker').setValue(''); // Clear the datepicker value
            }, 10);
        }); // Placeholder for the initialization content
    }
}

// FILE DOCUMENTED
